// CustomerCarePage.js

import React from 'react';
import {
    Container,
    Box,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Button,
    Grid,
    Link
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SEO from '../components/SEO';

// Helper to map string names to actual icon components
const iconMap = {
    "Phone Support": <PhoneIcon sx={{ color: 'green' }} />,
    "Email Support": <EmailIcon sx={{ color: 'green' }} />,
    "Business Hours": <AccessTimeIcon sx={{ color: 'green' }} />,
    "Mailing Address": <LocationOnIcon sx={{ color: 'green' }} />
};

// Safely parse the JSON string from the environment variable
let contactDetails = [];
try {
    contactDetails = JSON.parse(process.env.REACT_APP_CONTACT_DETAILS || '[]');
} catch (error) {
    console.error("Failed to parse contact details from environment variable:", error);
}

// Content is now sourced from environment variables
const content = {
    title: process.env.REACT_APP_CONTACT_TITLE || "Contact Us",
    intro: process.env.REACT_APP_CONTACT_INTRO || "We're here to help!",
    details: contactDetails,
};

function CustomerCarePage({ onOpenSupportForm }) {

    return (
        <>
            <SEO
                title="Contact Us | Customer Care | Matax Express"
                description="Get in touch with Matax Express customer support. We're here to help with orders, delivery questions, and wholesale inquiries in Toronto and GTA."
                keywords="contact Matax Express, wholesale grocery support, Toronto produce delivery help, customer service"
                url="https://mataxexpress.ca/care"
            />
            <Box sx={{ bgcolor: '#F7F9F2', minHeight: 'calc(100vh - 64px)', py: 8 }}>
                <Container maxWidth="md">
                    <Paper elevation={3} sx={{ p: { xs: 3, md: 5 }, borderRadius: 4 }}>
                        <Typography variant="h3" component="h1" fontWeight={700} gutterBottom textAlign="center" color="#75a84b">
                            {content.title}
                        </Typography>
                        <Typography variant="h6" component="p" textAlign="center" color="text.secondary" sx={{ mb: 6, maxWidth: '600px', mx: 'auto' }}>
                            {content.intro}
                        </Typography>

                        <Grid container spacing={4}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="h5" gutterBottom color="primary">Contact Details</Typography>
                                <List>
                                    {content.details && content.details.map((item, index) => (
                                        <ListItem key={index}>
                                            <ListItemIcon>
                                                {/* Use the iconMap to get the correct icon, with a fallback */}
                                                {item.type === "Phone Support" ? (
                                                    <Link href={`tel:${item.value.replace(/[^0-9+]/g, '')}`} underline="none">
                                                        {iconMap[item.type]}
                                                    </Link>
                                                ) : item.type === "Email Support" ? (
                                                    <Link href={`mailto:${item.value}`} underline="none">
                                                        {iconMap[item.type]}
                                                    </Link>
                                                ) : item.type === "Mailing Address" ? (
                                                    <Link
                                                        href="https://www.google.com/maps/place/Matax+Express+Ltd/data=!4m2!3m1!1s0x0:0x5a4cdf2c0519500c?sa=X&ved=1t:2428&ictx=111"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        underline="none"
                                                    >
                                                        {iconMap[item.type]}
                                                    </Link>
                                                ) : (
                                                    iconMap[item.type] || <LocationOnIcon sx={{ color: 'green' }} />
                                                )}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={item.type}
                                                secondary={
                                                    item.type === "Phone Support" ? (
                                                        <Link href={`tel:${item.value.replace(/[^0-9+]/g, '')}`} color="inherit" underline="hover">
                                                            {item.value}
                                                        </Link>
                                                    ) : item.type === "Email Support" ? (
                                                        <Link href={`mailto:${item.value}`} color="inherit" underline="hover">
                                                            {item.value}
                                                        </Link>
                                                    ) : item.type === "Mailing Address" ? (
                                                        <Link
                                                            href="https://www.google.com/maps/place/Matax+Express+Ltd/data=!4m2!3m1!1s0x0:0x5a4cdf2c0519500c?sa=X&ved=1t:2428&ictx=111"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            color="inherit"
                                                            underline="hover"
                                                        >
                                                            {item.value}
                                                        </Link>
                                                    ) : (
                                                        item.value
                                                    )
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Typography variant="h5" gutterBottom color="primary">Submit a Request</Typography>
                                <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                                    Have an issue with your order or need assistance? Click the button below to open our support assistant.
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    fullWidth
                                    onClick={onOpenSupportForm}
                                    startIcon={<SupportAgentIcon />}
                                    sx={{ py: 2 }}
                                >
                                    Open Support Form
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>
                </Container>
            </Box>
        </>
    );
}

export default CustomerCarePage;

